import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from 'reactstrap';

import apiOffers from 'api/offers';
import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getContributionMarginTotal from 'services/products/calculations/getContributionMarginTotal';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';
import 'style/offers/SearchPositionModal.css';

interface IRow {
  contributionMargin: number;
  contributionMarginRatio: number;
  expense: number;
  offerDate: string;
  position: ISearchedPosition;
  sellingPrice: number;
}

type SortKey =
  | 'position'
  | 'name'
  | 'quantity'
  | 'sellingPrice'
  | 'expense'
  | 'contributionMargin'
  | 'contributionMarginRatio'
  | 'target'
  | 'type'
  | 'status'
  | 'offeredBy'
  | 'offerDate';

const formatDate = (dateTime: string): string => {
  const [year, month, day] = dateTime.split(' ')[0].split('-');
  return `${day}.${month}.${year}`;
};

const getSortValue = (row: IRow, key: SortKey): string | number => {
  switch (key) {
    case 'position':
      return row.position.position ?? '';
    case 'name':
      return row.position.name ?? '';
    case 'quantity':
      return row.position.quantity ?? 0;
    case 'target':
      return row.position.targetName ?? '';
    case 'type':
      return row.position.offerType;
    case 'status':
      return row.position.offerStatus;
    case 'offeredBy':
      return row.position.offeredBy ?? '';
    case 'offerDate':
      return row.offerDate;
    default:
      return row[key];
  }
};

/**
 * Searches positions of all earlier offers and copies the content of the chosen position
 * to the offer as a new position.
 */
const SearchPositionModal = ({
  isOpen,
  offerId,
  onCopied,
  toggle,
  type,
}: {
  isOpen: boolean;
  offerId: number;
  onCopied: () => void;
  toggle: () => void;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);

  const [search, setSearch] = useState('');
  const [offerType, setOfferType] = useState('');
  const [status, setStatus] = useState('');
  const [group, setGroup] = useState('');
  const [positions, setPositions] = useState<ISearchedPosition[] | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('offerDate');
  const [sortAscending, setSortAscending] = useState(false);
  const [copyingId, setCopyingId] = useState<number | null>(null);

  const typeOptions = t(`${translations.offers}:types`, {
    returnObjects: true,
  }) as { [type: string]: string };
  const statusOptions = t(`${translations.offers}:statuses`, {
    returnObjects: true,
  }) as { [status: string]: string };
  const groupOptions = t(`${translations.products}:groups`, {
    returnObjects: true,
  }) as { [group: string]: string };

  // Search as the user types or changes a filter
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    setPositions(null);

    const timeout = setTimeout(async () => {
      try {
        const found = await apiOffers.positionSearch.get({
          group: group || undefined,
          search: search.trim() || undefined,
          status: status || undefined,
          type: offerType || undefined,
        });
        if (!cancelled) {
          setPositions(found);
        }
      } catch (error) {
        if (!cancelled) {
          setPositions([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isOpen, search, offerType, status, group]);

  // Economic figures of the primary calculation version of each position
  const rows: IRow[] = useMemo(
    () =>
      (positions ?? []).map((position) => {
        const primaryVersion = position.versions.find(
          (version) => version.isPrimary
        );
        const quantity = position.quantity ?? 0;

        const sellingPrice =
          primaryVersion === undefined
            ? 0
            : getSellingPriceTotal(
                primaryVersion.installations,
                primaryVersion.subproducts,
                quantity
              );
        const expense =
          primaryVersion === undefined
            ? 0
            : getExpenseTotal(
                primaryVersion.installations,
                primaryVersion.subproducts,
                quantity
              );
        const contributionMargin =
          primaryVersion === undefined
            ? 0
            : getContributionMarginTotal(
                primaryVersion.installations,
                primaryVersion.subproducts,
                quantity
              );

        return {
          contributionMargin,
          contributionMarginRatio:
            sellingPrice === 0 ? 0 : (contributionMargin / sellingPrice) * 100,
          expense,
          offerDate: position.offerDate,
          position,
          sellingPrice,
        };
      }),
    [positions]
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const first = getSortValue(a, sortKey);
        const second = getSortValue(b, sortKey);
        const result =
          typeof first === 'number' && typeof second === 'number'
            ? first - second
            : String(first).localeCompare(String(second), 'fi', {
                numeric: true,
              });
        return sortAscending ? result : -result;
      }),
    [rows, sortKey, sortAscending]
  );

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(true);
    }
  };

  const copy = async (position: ISearchedPosition) => {
    setCopyingId(position.offerProductId);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.copiedPositions.post(
          offerId,
          position.offerProductId
        );
      } else {
        await apiOffers.copiedPositions.post(offerId, position.offerProductId);
      }

      dispatch(addMessage(t(`${translations.offers}:positionSearch.Copied`)));
      onCopied();
      toggle();
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:positionSearch.Copy failed`))
      );
    } finally {
      setCopyingId(null);
    }
  };

  const header = (key: SortKey, text: string, numeric = false) => {
    const sorted = sortKey === key;
    const direction = sortAscending ? 'ascending' : 'descending';
    const arrow = sortAscending ? ' ▲' : ' ▼';

    return (
      <th
        className={`sortable${numeric ? ' numeric' : ''}${
          sorted ? ' sorted' : ''
        }`}
        onClick={() => sortBy(key)}
        aria-sort={sorted ? direction : 'none'}
      >
        {text}
        {sorted ? arrow : ''}
      </th>
    );
  };

  const unitText = (unit: string) => t(`${translations.common}:units:${unit}`);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="search-position-modal"
    >
      <ModalHeader className="modal-header" toggle={toggle}>
        {t(`${translations.offers}:positionSearch.Title`)}
      </ModalHeader>
      <ModalBody>
        <Row className="search-position-filters">
          <Col md={5}>
            <Input
              autoFocus
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t(`${translations.offers}:positionSearch.Search`)}
              value={search}
            />
          </Col>
          <Col md={2}>
            <Input
              aria-label={t(`${translations.offers}:Offer Type`)}
              onChange={(event) => setOfferType(event.target.value)}
              type="select"
              value={offerType}
            >
              <option value="">
                {t(`${translations.offers}:positionSearch.All types`)}
              </option>
              {Object.entries(typeOptions).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Input
              aria-label={t(`${translations.offers}:Status`)}
              onChange={(event) => setStatus(event.target.value)}
              type="select"
              value={status}
            >
              <option value="">
                {t(`${translations.offers}:positionSearch.All statuses`)}
              </option>
              {Object.entries(statusOptions).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={3}>
            <Input
              aria-label={t(`${translations.offers}:positionSearch.Work type`)}
              onChange={(event) => setGroup(event.target.value)}
              type="select"
              value={group}
            >
              <option value="">
                {t(`${translations.offers}:positionSearch.All work types`)}
              </option>
              {Object.entries(groupOptions).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Input>
          </Col>
        </Row>

        {positions === null ? (
          <p className="search-position-info">
            {t(`${translations.offers}:positionSearch.Searching`)}
          </p>
        ) : (
          <p className="search-position-info">
            {t(`${translations.offers}:positionSearch.Results`, {
              count: positions.length,
            })}
            {positions.length >= 200
              ? ` ${t(`${translations.offers}:positionSearch.Limited`)}`
              : ''}
          </p>
        )}

        {sortedRows.length > 0 ? (
          <div className="search-position-table">
            <Table size="sm" hover>
              <thead>
                <tr>
                  {header('position', t(`${translations.products}:Position`))}
                  {header('name', t(`${translations.products}:Product`))}
                  {header(
                    'quantity',
                    t(`${translations.common}:Quantity`),
                    true
                  )}
                  {header(
                    'sellingPrice',
                    t(`${translations.offers}:Selling price`),
                    true
                  )}
                  {header(
                    'expense',
                    t(`${translations.offers}:Expenses total`),
                    true
                  )}
                  {header(
                    'contributionMargin',
                    `${t(`${translations.offers}:Contribution margin`)} €`,
                    true
                  )}
                  {header(
                    'contributionMarginRatio',
                    `${t(`${translations.offers}:Contribution margin`)} %`,
                    true
                  )}
                  {header('target', t(`${translations.offers}:Project`))}
                  {header('type', t(`${translations.offers}:Offer Type`))}
                  {header('status', t(`${translations.offers}:Status`))}
                  {header(
                    'offeredBy',
                    t(`${translations.offers}:positionSearch.Offered by`)
                  )}
                  {header(
                    'offerDate',
                    t(`${translations.offers}:positionSearch.Offer date`)
                  )}
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.position.offerProductId}>
                    <td>{row.position.position}</td>
                    <td>{row.position.name}</td>
                    <td className="numeric">
                      {`${formatGroupedNumber(
                        row.position.quantity ?? 0,
                        2,
                        0
                      )} ${unitText(row.position.unit)}`}
                    </td>
                    <td className="numeric">
                      {`${formatGroupedNumber(row.sellingPrice)} €`}
                    </td>
                    <td className="numeric">
                      {`${formatGroupedNumber(row.expense)} €`}
                    </td>
                    <td className="numeric">
                      {`${formatGroupedNumber(row.contributionMargin)} €`}
                    </td>
                    <td className="numeric">
                      {`${formatGroupedNumber(row.contributionMarginRatio)} %`}
                    </td>
                    <td>
                      {row.position.targetName}
                      {row.position.revisionNumber > 1
                        ? ` (v${row.position.revisionNumber})`
                        : ''}
                    </td>
                    <td>
                      {typeOptions[row.position.offerType] ??
                        row.position.offerType}
                    </td>
                    <td>
                      <span
                        className={`search-position-status status-${row.position.offerStatus}`}
                      >
                        {statusOptions[row.position.offerStatus] ??
                          row.position.offerStatus}
                      </span>
                    </td>
                    <td>{row.position.offeredBy}</td>
                    <td>{formatDate(row.offerDate)}</td>
                    <td>
                      <Button
                        className="modal-button"
                        disabled={copyingId !== null}
                        onClick={() => copy(row.position)}
                        size="sm"
                      >
                        {t(`${translations.offers}:positionSearch.Copy`)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button className="modal-button" onClick={toggle}>
          {t(`${translations.offers}:Close`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SearchPositionModal;
